import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  HANDYMAN_SPECIALTIES,
  type HandymanSpecialtyCode,
  type HandymanSpecialtySelection,
} from "@/lib/handymanConfig";
import { useLanguage } from "@/hooks/useLanguage";

const MAX_SELECTIONS = 5;

interface Props {
  value: HandymanSpecialtySelection[];
  onChange: (v: HandymanSpecialtySelection[]) => void;
  error?: string;
}

export default function HandymanSpecialtiesSelector({ value, onChange, error }: Props) {
  const { t, locale } = useLanguage("artisan");
  const selectedCodes = value.map((s) => s.specialty_code);
  const otherSelection = value.find((s) => s.specialty_code === "other");

  const handleCheck = (code: HandymanSpecialtyCode, checked: boolean) => {
    if (checked) {
      if (selectedCodes.length >= MAX_SELECTIONS) return;
      onChange([...value, { specialty_code: code }]);
    } else {
      onChange(value.filter((s) => s.specialty_code !== code));
    }
  };

  const handleOtherLabel = (label: string) => {
    onChange(
      value.map((s) =>
        s.specialty_code === "other" ? { ...s, other_specialty_label: label } : s
      )
    );
  };

  return (
    <div className="rounded-xl border bg-card p-4 space-y-3">
      <p className="text-xs text-muted-foreground font-medium">
        {t("handyman.select_hint", { max: MAX_SELECTIONS })}
      </p>

      <div className="space-y-2.5">
        {HANDYMAN_SPECIALTIES.map((specialty) => {
          const isChecked = selectedCodes.includes(specialty.code);
          const isDisabled = !isChecked && selectedCodes.length >= MAX_SELECTIONS;
          const label = locale === "ar" ? specialty.label_ar : specialty.label_fr;

          return (
            <div key={specialty.code} className="space-y-1.5">
              <div className="flex items-center gap-2.5">
                <Checkbox
                  id={`hs-${specialty.code}`}
                  checked={isChecked}
                  disabled={isDisabled}
                  onCheckedChange={(checked) =>
                    handleCheck(specialty.code, checked === true)
                  }
                />
                <Label
                  htmlFor={`hs-${specialty.code}`}
                  className={`text-sm font-normal cursor-pointer select-none ${
                    isDisabled ? "text-muted-foreground" : ""
                  }`}
                >
                  {label}
                </Label>
              </div>

              {specialty.is_other && isChecked && (
                <div className="ms-6">
                  <Input
                    placeholder={t("handyman.other_placeholder")}
                    maxLength={50}
                    value={otherSelection?.other_specialty_label ?? ""}
                    onChange={(e) => handleOtherLabel(e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
